import React, { useState, useEffect, useRef } from 'react';
import { Trash2, Edit, X, Search, Download, Upload, Filter, Eye, EyeOff } from 'lucide-react';
import ReusableTable from '../../dashboard/ReusableTable';
import ReusableForm from '../../common/ReusableForm';
import ReusableDeleteDialog from '../../common/ReusableDeleteDialog';
import { getRequest, postRequest } from '../../services/apiService';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';

const UserManagement = () => {
  const toast = useRef(null);
  const [users, setUsers] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [units, setUnits] = useState([]);
  const [formData, setFormData] = useState({
    id: null,
    first_name: '',
    last_name: '',
    personal_number: '',
    loginname: '',
    email: '',
    password: '',
    user_role_id: '',
    hrcdf_designation: '',
    unit: '',
    status: 1,
    ad_user: false,
    rankCode: '',
    rankName: '',
    user_type: '',
    is_ad_user: false,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hrcdfFetching, setHrcdfFetching] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 10,
    page: 1,
    sortField: null,
    sortOrder: null,
    filters: {}
  });
  const [deleteFormData, setDeleteFormData] = useState({ confirmation: "" });
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);
  const [passwordVisible, setPasswordVisible] = useState(false);

  useEffect(() => {
    fetchUnits();
    fetchUserRoles();
  }, []);

  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      fetchUsers();
    }, 700);
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [search, lazyParams.page, lazyParams.rows]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await getRequest(`api/auth/users/?page=${lazyParams.page}&search=${encodeURIComponent(search)}`);
      const mapped = response.results.data.map(user => ({
        ...user,
        rank_code: user.rankCode || '',
        login_name: user.loginname || '',
        unit: user.unit?.name || '',
        role: user.roles?.[0]?.name || ''
      }));
      setUsers(mapped);
      setTotalRecords(response.count);
    } catch (e) {
      console.error(e);
      showNotification('Failed to fetch users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserRoles = async () => {
    try {
      const res = await getRequest('access/user-roles/');
      setUserRoles(Array.isArray(res) ? res : []);
    } catch (e) {
      console.error(e);
      showNotification('Failed to fetch user roles', 'error');
    }
  };

  const fetchUnits = async () => {
    try {
      const res = await getRequest('/master/units/');
      setUnits(res?.data || []);
    } catch (e) {
      console.error(e);
      showNotification('Failed to fetch units', 'error');
    }
  };

  const fetchHrcdfData = async () => {
    if (!formData.personal_number) {
      showNotification('Please enter a Personal Number to fetch HRCDF data.', 'warn');
      return;
    }
    setHrcdfFetching(true);
    try {
      // Assuming an API endpoint for HRCDF data exists
      // Replace with your actual HRCDF data endpoint
      const response = await getRequest(`api/auth/users/hrcdf?personal_number=${encodeURIComponent(formData.personal_number)}`);
      if (response && response.data) {
        const hrcdfUser = response.data;
        setFormData(prev => ({
          ...prev,
          first_name: hrcdfUser.first_name || '',
          last_name: hrcdfUser.last_name || '',
          rankCode: hrcdfUser.rankCode || '',
          rankName: hrcdfUser.rankName || '',
          hrcdf_designation: hrcdfUser.hrcdf_designation || '',
          email: hrcdfUser.email || '',
          loginname: hrcdfUser.loginname || '',
          user_type: hrcdfUser.user_type || '',
        }));
        showNotification('HRCDF data fetched successfully!', 'success');
      } else {
        showNotification('No HRCDF data found for this Personal Number.', 'warn');
      }
    } catch (e) {
      console.error("Error fetching HRCDF data:", e);
      let errorMessage = 'Failed to fetch HRCDF data.';
      if (e.response && e.response.data && e.response.data.message) {
        errorMessage = e.response.data.message;
      }
      showNotification(errorMessage, 'error');
    } finally {
      setHrcdfFetching(false);
    }
  };

  const resetForm = () => {
    setFormData({
      id: null,
      first_name: '',
      last_name: '',
      personal_number: '',
      loginname: '',
      email: '',
      password: '',
      user_role_id: '',
      hrcdf_designation: '',
      unit: '',
      status: 1,
      ad_user: false,
      rankCode: '',
      rankName: '',
      user_type: '',
      is_ad_user: false,
    });
    setIsEditing(false);
    setPasswordVisible(false);
  };

  const showNotification = (message, type) => {
    toast.current.show({
      severity: type,
      summary: type === 'success' ? 'Success' : type === 'warning' ? 'Warning' : 'Error',
      detail: message,
      life: 3000
    });
  };

  const handleSubmit = async () => {
    try {
      if (isEditing) {
        const payload = {
          id: formData.id,
          user_role: parseInt(formData.user_role_id),
          unit: parseInt(formData.unit),
          first_name: formData.first_name,
          last_name: formData.last_name,
          personal_number: formData.personal_number,
          loginname: formData.loginname,
          email: formData.email,
          hrcdf_designation: formData.hrcdf_designation,
          status: formData.status,
          ad_user: formData.is_ad_user,
          rankCode: formData.rankCode,
          rankName: formData.rankName,
          user_type: formData.user_type,
          is_ad_user: formData.is_ad_user,
        };

        if (formData.password && !formData.is_ad_user) {
          payload.password = formData.password;
        } else if (formData.is_ad_user) {
            delete payload.password;
        }

        await postRequest('api/auth/users/', payload);
        showNotification('User updated successfully', 'success');
      } else {
        const payload = {
          ...formData,
          user_role: parseInt(formData.user_role_id),
          unit: parseInt(formData.unit),
          ad_user: formData.is_ad_user,
        };

        if (payload.is_ad_user) {
            delete payload.password;
        }

        await postRequest('api/auth/users/', payload);
        showNotification('User added successfully', 'success');
      }

      resetForm();
      setIsModalOpen(false);
      fetchUsers();
    } catch (e) {
      console.error(e);
      let errorMessage = 'Failed to save user';
      if (e.response && e.response.data) {
        if (e.response.data.password && Array.isArray(e.response.data.password) && e.response.data.password.includes('This field is required.')) {
          errorMessage = 'Password: This field is required.';
        } else if (e.response.data.message) {
          errorMessage = e.response.data.message;
        } else if (typeof e.response.data === 'object') {
          const fieldErrors = Object.values(e.response.data).flat();
          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors.join(', ');
          }
        }
      }
      showNotification(errorMessage, 'error');
    }
  };

  const handleEdit = user => {
   const unitId = units.find(u => u.name === user.unit)?.id || '';

    setFormData({
      id: user.id,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      personal_number: user.personal_number || '',
      loginname: user.loginname || '',
      email: user.email || '',
      password: '',
      user_role_id: user.user_role || '',
      hrcdf_designation: user.hrcdf_designation || '',
      unit: unitId,
      status: user.status || 1,
      ad_user: user.ad_user,
      rankCode: user.rankCode || '',
      rankName: user.rankName || '',
      user_type: user.user_type || '',
      is_ad_user: user.ad_user || false,
    });
    setIsEditing(true);
    setIsModalOpen(true);
    setPasswordVisible(false);
  };

  const openDeleteModal = user => {
    setUserToDelete(user);
    setDeleteFormData({ confirmation: '' });
    setShowDeleteDialog(true);
  };

  const confirmDelete = async (e) => {
    e.preventDefault();
    if (deleteFormData.confirmation !== 'DELETE') {
      showNotification('Please type DELETE to confirm', 'error');
      return;
    }

    if (!userToDelete) return;

    try {
      setLoading(true);
      const payload = {
        id: userToDelete.id,
        status_db: 3
      };
      await postRequest('api/auth/users/', payload);
      showNotification('User deleted successfully', 'warning');
      setShowDeleteDialog(false);
      setUserToDelete(null);
      setDeleteFormData({ confirmation: '' });
      fetchUsers();
    } catch (e) {
      console.error(e);
      showNotification('Failed to delete user', 'error');
    } finally {
      setLoading(false);
    }
  };

  const onPageChange = (event) => {
    setLazyParams({
      ...lazyParams,
      first: event.first,
      rows: event.rows,
      page: event.page + 1
    });
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(prev => !prev);
  };

  const formFields = [
     {
      name: 'is_ad_user',
      label: 'AD User',
      type: 'checkbox',
      required: false
    },
    {
      name: 'personal_number',
      label: 'Personal Number',
      type: 'text',
      required: formData.is_ad_user, // Mandatory if AD User is checked
      helpText: formData.is_ad_user ? 'Required for AD Users (numbers only)' : 'Optional',
      // Add an onChange handler to filter non-numeric input
      onChange: (e) => {
        const numericValue = e.target.value.replace(/[^0-9]/g, ''); // Keep only digits
        setFormData(prev => ({ ...prev, personal_number: numericValue }));
      },
      // You can still keep pattern and inputMode as hints for browsers
      pattern: '^[0-9]*$',
      inputMode: 'numeric'
    },
    // Conditionally add the button field right after personal_number if AD user and personal number is entered
    (formData.is_ad_user && formData.personal_number) && {
      name: 'fetch_hrcdf_data_button', // A unique identifier for this "field"
      type: 'custom', // Use 'custom' type to render a custom component
      component: () => ( // This component function will be rendered by ReusableForm
        <Button
          type="button"
          label={hrcdfFetching ? "Fetching..." : "Fetch Data from HRCDF"}
          icon="pi pi-download"
          onClick={fetchHrcdfData}
          className="px-4 py-3 border border-gray-300 rounded-md bg-[#1e3a8a] text-white hover:bg-[#1e3a8a]/90 mt-8" // Add margin for spacing
          disabled={hrcdfFetching}
        />
      ),
      // Set visible to true explicitly as we are controlling visibility outside the field object
      visible: true
    },
    { name: 'rankName', label: 'Rank Name', type: 'text', required: true },
    { name: 'rankCode', label: 'Rank Code', type: 'text', required: true },
    { name: 'first_name', label: 'First Name', type: 'text', required: true },
    { name: 'last_name', label: 'Last Name', type: 'text', required: true },
    { name: 'loginname', label: 'Login Name', type: 'text', required: true },
    { name: 'email', label: 'Email', type: 'text', required: true },
    (!isEditing && !formData.is_ad_user) && {
      name: 'password',
      label: 'Password',
      type: 'password',
      actualInputType: passwordVisible ? 'text' : 'password',
      required: true,
      suffix: (
        <button type="button" onClick={togglePasswordVisibility} className="p-0 border-none bg-transparent">
          {passwordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      )
    },
    { name: 'hrcdf_designation', label: 'HRCDF Designation', type: 'text', required: true },
    {
      name: 'unit',
      label: 'Unit',
      type: 'select',
      required: true,
      options: units.map(u => ({ label: u.name, value: u.id }))
    },
    {
      name: 'user_role_id',
      label: 'User Role',
      type: 'select',
      required: true,
      options: userRoles.map(role => ({
        label: `${role.name}`,
        value: role.id
      }))
    }
  ].filter(Boolean); // Filter out any `false` or `null` entries from conditional rendering

  const columnFields = [
    { field: 'rank_code', header: 'Rank Code' },
    { field: 'first_name', header: 'First Name' },
    { field: 'last_name', header: 'Last Name' },
    { field: 'login_name', header: 'Login Name' },
    { field: 'unit', header: 'Unit' },
    { field: 'role', header: 'Role' }
  ];

  const actionItems = [
    { label: 'Edit', icon: 'pi pi-pencil', command: handleEdit },
    { label: 'Delete', icon: 'pi pi-trash', command: openDeleteModal }
  ];

  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirm Deletion",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  return (
    <div className=" bg-gray-50 min-h-screen">
      <Toast ref={toast} position="top-right" />

      <div className="bg-white rounded-lg shadow-sm">
        <ReusableTable
          title="User Management"
          data={users}
          columnFields={columnFields}
          actionMenu
          actionItems={actionItems}
          loading={loading}
          totalRecords={totalRecords}
          onPageChange={onPageChange}
          first={lazyParams.first}
          className="border-none"
          searchValue={search}
          onSearchChange={setSearch}
          setAddNewDialogVisible={() => {
            resetForm();
            setIsModalOpen(true);
          }}
        />
      </div>

      <Dialog
        visible={isModalOpen}
        headerClassName="bg-[#1e3a8a] text-white px-6 py-4 rounded-t-lg"
        header={isEditing ? 'Edit User' : 'Add New User'}
        onHide={() => { setIsModalOpen(false); resetForm(); }}
        className="max-w-[90vw] w-full rounded-xl"
        contentClassName="p-4"
        style={{ maxWidth: formFields.length > 8 ? '1200px' : '800px' }}
      >
        <ReusableForm
          formData={formData}
          setFormData={setFormData}
          fields={formFields}
          onSubmit={handleSubmit}
          onHide={() => { setIsModalOpen(false); resetForm(); }}
        />
      </Dialog>

      <ReusableDeleteDialog
        visible={showDeleteDialog}
        onHide={() => {
          setShowDeleteDialog(false);
          setDeleteFormData({ confirmation: "" });
        }}
        onDelete={confirmDelete}
        fields={deleteFormFields}
        formData={deleteFormData}
        setFormData={setDeleteFormData}
        loading={loading}
        title="Delete User"
        description={userToDelete && `You are about to delete user "${userToDelete.first_name} ${userToDelete.last_name}". This action cannot be undone.`}
        deleteLabel="Delete User"
      />
    </div>
  );
};

export default UserManagement;